import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Document,
  DocumentStatus,
  ImageMedia,
  ImageMediaPost,
  VideoMedia,
  VideoMediaPost,
} from 'src/app/models/device-documentation/device-documentation.model';
import { AuthService } from '../../authentication/auth.service';
import { BaseApiService } from '../../base-api.service';
import { TranslateFirebaseService } from '../../utils/translate-firebase.service';

@Injectable()
export class RestDeviceDocumentationService extends BaseApiService {
  constructor(
    httpClient: HttpClient,
    translate: TranslateFirebaseService,
    private authService: AuthService
  ) {
    super(httpClient, translate);
  }

  getDocumentsByArticleId(articleId: number): Observable<Document[]> {
    return this.httpClient.get<Document[]>(
      super.getUpsApiHost() + 'Documents/Article/' + articleId,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  getDocumentsBySerialNumber(serialNumberId: number): Observable<Document[]> {
    return this.httpClient.get<Document[]>(
      super.getUpsApiHost() + 'Documents/SerialNumber/' + serialNumberId,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  getAllDocumentsByArticleIdAndLanguageId(
    articleId: number,
    languageId: string
  ): Observable<Document[]> {
    return this.httpClient.get<Document[]>(
      super.getUpsApiHost() +
        'Documents/Article/' +
        articleId +
        '/' +
        languageId,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  // replaced with getMediaByArticleIdandLanguageId
  getMediaByArticleId(
    articleId: number
  ): Observable<ImageMedia[] | VideoMedia[]> {
    return this.httpClient.get<ImageMedia[] | VideoMedia[]>(
      super.getUpsApiHost() + 'Media/article/' + articleId,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  getMediaByArticleIdandLanguageId(
    articleId: number,
    languageId: string = 'en'
  ): Observable<ImageMedia[] | VideoMedia[]> {
    return this.httpClient.get<ImageMedia[] | VideoMedia[]>(
      `${super.getUpsApiHost()}Media/article/${articleId}/${languageId}`,
      { headers: this.authService.upsHttpHeaders }
    );
  }
  getVideoByArticleIdandLanguageId(
    articleId: number,
    languageId: string = 'en'
  ): Observable<VideoMedia[]> {
    return this.httpClient.get<VideoMedia[]>(
      `${super.getUpsApiHost()}Media/article/${articleId}/${languageId}`,
      { headers: this.authService.upsHttpHeaders }
    ).pipe(map((media) => media.filter((video => video?.contentType.toString().startsWith('video/')))));
  }

  uploadNewImageMedia(media: FormData): Observable<ImageMedia> {
    return this.httpClient.post<ImageMedia>(
      super.getUpsApiHost() + 'Media',
      media,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  uploadNewVideoMediaExternal(media: VideoMediaPost): Observable<VideoMedia> {
    return this.httpClient.post<VideoMedia>(
      super.getUpsApiHost() + 'Media/external',
      media,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  uploadNewVideoMedia(media: FormData): Observable<VideoMedia> {
    return this.httpClient.post<VideoMedia>(
      super.getUpsApiHost() + 'Media',
      media,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  uploadDocument(document: FormData): Observable<Document> {
    return this.httpClient.post<Document>(
      super.getUpsApiHost() + 'Documents/Advanced',
      document,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  updateDocument(documentId: number, document: FormData): Observable<Document> {
    return this.httpClient.put<Document>(
      super.getUpsApiHost() + 'Documents/Advanced/' + documentId,
      document,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  changeDocumentStatus(documentId: number, publish: number): Observable<any> {
    return this.httpClient.put<any>(
      super.getUpsApiHost() + 'Documents/Status/' + documentId + '/' + publish,
      null,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  deleteMedia(id: number): Observable<any> {
    return this.httpClient.delete<any>(
      super.getUpsApiHost() + 'Media/entity/' + id,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  deleteDocumentEntity(id: number): Observable<any> {
    return this.httpClient.delete<any>(
      super.getUpsApiHost() + 'Documents/entity/' + id,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  deleteDocument(articleId: number, documentId: number): Observable<any> {
    return this.httpClient.delete<any>(
      super.getUpsApiHost() +
        'Documents/Article/' +
        articleId +
        '/' +
        documentId,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  updateDocumentOrder(articleId: number, obj: any): Observable<any> {
    return this.httpClient.put<any>(
      super.getUpsApiHost() + 'Documents/ArticleOrder/' + articleId,
      obj,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  updateMediaOrder(articleId: number, obj: any): Observable<any> {
    return this.httpClient.put<any>(
      super.getUpsApiHost() + 'Media/ArticleOrder/' + articleId,
      obj,
      { headers: this.authService.upsHttpHeaders }
    );
  }
}
